import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { AI_MANAGER } from '@/constants/testIds';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';

const STORAGE_KEY = (id) => `maarivil-mgr-${id}`;

const AITalentManagerChat = ({ artist }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    const k = STORAGE_KEY(artist.artist_id);
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const id = `s_${Math.random().toString(36).slice(2, 12)}`;
    sessionStorage.setItem(k, id);
    return id;
  });
  const scroller = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      // load history if any
      api.get(`/ai/manager/${artist.artist_id}/history`, { params: { session_id: sessionId } })
        .then(({ data }) => {
          if (data.messages?.length) {
            setMessages(data.messages.map((m, idx) => ({ id: m.chat_id || `h-${idx}`, role: m.role, content: m.content })));
          } else {
            setMessages([{
              id: 'greeting',
              role: 'assistant',
              content: `Hi! I'm ${artist.stage_name}'s AI manager. Tell me about your event — type, date, city, vibe — and I'll share availability and a rough quote. Ready to book? Hit the "Book this artist" button anytime.`,
            }]);
          }
        });
    }
  }, [open, artist.artist_id, artist.stage_name, sessionId, messages.length]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: 'user', content: text }]);
    setInput('');
    setSending(true);
    try {
      const { data } = await api.post(`/ai/manager/${artist.artist_id}`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: 'assistant', content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: 'assistant', content: "Sorry — I'm momentarily offline. Please try again or use the Book button to send an inquiry." }]);
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Floating toggle */}
      <button
        onClick={() => setOpen(true)}
        data-testid={AI_MANAGER.chatToggle}
        className={`fixed bottom-24 md:bottom-6 right-6 z-40 btn-rainbow rounded-full h-14 px-5 text-white shadow-2xl shadow-fuchsia-500/30 flex items-center gap-2 transition-all ${open ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
      >
        <Sparkles className="h-5 w-5" />
        <span className="hidden sm:inline font-medium">Chat with AI manager</span>
      </button>

      {/* Chat panel */}
      <div
        className={`fixed bottom-24 md:bottom-6 right-6 z-50 w-[92vw] sm:w-[400px] h-[560px] max-h-[75vh] md:max-h-[80vh] glass-strong rounded-3xl border border-white/10 shadow-2xl flex flex-col transition-all overflow-hidden ${open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}`}
      >
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <img src={artist.profile_image} alt="" className="h-10 w-10 rounded-full object-cover" />
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-display font-semibold truncate">{artist.stage_name}'s AI manager</div>
            <div className="text-[10px] uppercase tracking-widest ai-text">● online</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid={AI_MANAGER.closeButton}
            className="text-slate-400 hover:text-white"
          ><X className="h-5 w-5" /></button>
        </div>

        <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'btn-rainbow text-white' : 'bg-white/5 border border-white/10 text-slate-100'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-slate-400 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="p-3 border-t border-white/10 flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell me about your event…"
            data-testid={AI_MANAGER.chatInput}
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-fuchsia-400/40"
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            data-testid={AI_MANAGER.sendButton}
            className="btn-rainbow rounded-full h-10 w-10 flex items-center justify-center text-white disabled:opacity-50"
          ><Send className="h-4 w-4" /></button>
        </form>
      </div>
    </>
  );
};

export default AITalentManagerChat;
